import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { ReferenceType } from "../FpAppV4/utils/fpAppV4Interface";
import { getSourceRefs } from "./services/getSourceRefs";
import { Popover } from "@mui/material";
import clsx from "clsx";
import { setFileViewerData } from "@/redux/fileViewerSlice";

const CiteSource = ({ id, qid }: { id: string; qid: string }) => {
  const [sourceRefs, setSourceRefs] = useState<ReferenceType[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLSpanElement | null>(null);
  const [displaySrcNum, setDisplaySrcNum] = useState<number | null>(null);
  const dispatch = useDispatch();

  const updateSourceRefs = async (qid: string, id: string) => {
    if (!qid || !id) return;
    const sourceRefs = await getSourceRefs(qid, id);
    setSourceRefs(sourceRefs);
    setDisplaySrcNum(sourceRefs[0]?.displaySrcNum);
  };

  useEffect(() => {
    updateSourceRefs(qid, id);
  }, [qid, id]);

  const clickRef = (ref: ReferenceType, parentElementText?: string) => {
    // IODL/BNYM citation: open the IODL source viewer (SQL + result table)
    // instead of a document, mirroring the RMS citation branch in CitationNode.
    if ((ref as { iodlSource?: boolean }).iodlSource) {
      const iodlRef = ref as {
        sql?: string;
        columns?: string[];
        rowCount?: number;
        reasoning?: string;
        // Which data set this came from. Omitting it here is not harmless: the
        // viewer falls back to naming itself "IODL", so a second SQL-backed
        // source opened a panel titled after the first one.
        sourceLabel?: string | null;
      };
      dispatch(
        setFileViewerData({
          isOpen: true,
          fileType: "iodl-source",
          sql: iodlRef.sql,
          sourceLabel: iodlRef.sourceLabel ?? null,
          iodlColumns: iodlRef.columns,
          rowCount: iodlRef.rowCount,
          iodlReasoning: iodlRef.reasoning,
          iodlQid: qid,
        })
      );
      return;
    }
    dispatch(
      setFileViewerData({
        isOpen: true,
        fileId: ref.docId || "",
        fileName: ref.blobFilename || ref.parentDoc?.docTitle || "",
        fileType: "excel",
        highlightContext: ref.smallContext || ref.content,
        highlightPageNum: ref.pageNum,
        parentElementText,
      })
    );
  };

  const getParentElementText = (e: React.MouseEvent<HTMLSpanElement>) => {
    const parent = (e.target as HTMLSpanElement)?.parentElement?.parentElement
      ?.parentElement;
    if (!parent) return "";
    const parentNodeCopy = parent.cloneNode(true) as HTMLElement;
    const citationSpans = parentNodeCopy.querySelectorAll(".node-citation");
    citationSpans.forEach((span) => span.remove());

    return parentNodeCopy.textContent?.trim() || "";
  };

  const handleCiteSourceClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    const length = sourceRefs.length;
    if (length > 1) {
      setAnchorEl(e.currentTarget);
    }
    if (length === 1) {
      const ref = sourceRefs[0];
      const parentElementText = getParentElementText(e);
      clickRef(ref, parentElementText);
    }
  };

  return (
    <>
      <span
        className={clsx(
          "node-citation rounded-full mr-1 bg-gray-400 text-gray-100 text-[8px] p-0.5 px-1 cursor-pointer align-top h-fit"
        )}
        onClick={(e) => {
          console.log(id, qid, sourceRefs);
          handleCiteSourceClick(e);
        }}
      >
        {displaySrcNum || "1"}
      </span>
      <Popover
        id={anchorEl ? "simple-popover" : undefined}
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={(e, reason) => {
          setAnchorEl((prev) => {
            console.log("prev", prev);
            return null;
          });
          console.log("close", reason);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <div className="p-2 border-gray-300 border-2 pt-0 max-h-[300px] overflow-y-auto bg-gray-200">
          {sourceRefs.map((ref, index) => (
            <div
              key={index}
              onClick={() => {
                clickRef(ref);
              }}
              className="border w-[400px] p-2 overflow-hidden max-h-[100px] mt-2 cursor-pointer hover:bg-gray-800 bg-gray-500 text-white rounded-md transition-all duration-300"
            >
              <div>{ref.parentDoc?.docTitle}</div>
              <div className="text-[12px]">Page {ref.pageNum}</div>
              <div className="text-[10px]">{ref.smallContext}</div>
            </div>
          ))}
        </div>
      </Popover>
    </>
  );
};

export default CiteSource;
